import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { Typography } from '@components/Typography';
import { InternalLink } from '@components/Link';
import { PhonePreviewFrame } from '@components/PhonePreviewFrame';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { useLocations, useTranslation } from '@hooks';
import { getCompanyAddress } from '@helpers/companies';
import { CouponStatus } from '@constants/coupons';
import { LOCALE } from '@constants/locale';
import { RoutePath } from '@constants/routes';
import { InfoOutlinedIcon } from 'assets/images/icons';

const PreviewCoupon = ({ brands }) => {
    const account = useSelector(state => state.account?.account);

    const { watch } = useFormContext();
    const { t } = useTranslation();
    const { locationsMap } = useLocations();
    const iframeRef = useRef(null);

    const title = watch('title');
    const type = watch('type');
    const percentageOff = watch('percentageOff');
    const amountOff = watch('amountOff');
    const freeItem = watch('freeItem');
    const minimumAmount = watch('minimumAmount');
    const minimumAmountCurrency = watch('minimumAmountCurrency');
    const offerEndEnabled = watch('offerEndEnabled');
    const offerEndDate = watch('offerEndDate');
    const coverPhotoEnabled = watch('coverPhotoEnabled');
    const coverPhotoUrl = watch('coverPhotoUrl');
    const pinEnabled = watch('pinEnabled');
    const redemptionPeriodEnabled = watch('redemptionPeriodEnabled');
    const redemptionPeriodInDays = watch('redemptionPeriodInDays');
    const locationId = watch('locationId');
    const brandId = watch('brandId');
    const locationType = watch('locationType');
    const brandAccess = watch('brandAccess');

    const getPreviewData = () => {
        let header = '';
        const locations = [];
        let appearance = null;

        if (locationType === LOCATION_TYPE.BRAND && brandAccess && brandId) {
            const brand = brands.find(b => b.id === brandId);
            header = brand?.title || '';
            appearance = brand?.appearance || null;
            const brandLocations = Object.values(locationsMap).filter(loc => loc.brand?.id === brandId);
            brandLocations.forEach(loc => {
                locations.push({
                    name: loc.locationName || loc.internalName,
                    address: getCompanyAddress(loc.addressLine1, loc.city),
                });
            });
        } else if (locationId) {
            const selectedLocation = locationsMap[locationId] || {};
            header = selectedLocation.locationName || selectedLocation.internalName || '';
            locations.push({
                name: selectedLocation.locationName || selectedLocation.internalName || '',
                address: getCompanyAddress(selectedLocation.addressLine1, selectedLocation.city),
            });
            const locationBrand = brands.find(b => b.id === selectedLocation.brand?.id);
            appearance = locationBrand?.appearance || null;
        }

        let offerEnd = null;
        if (offerEndEnabled && offerEndDate) {
            offerEnd = new Date(offerEndDate);
        }
        let redemptionEnd = null;
        if (redemptionPeriodEnabled && redemptionPeriodInDays) {
            redemptionEnd = new Date();
            redemptionEnd.setDate(redemptionEnd.getDate() + Number(redemptionPeriodInDays));
        }
        let validUntil = null;
        if (offerEnd && redemptionEnd) {
            validUntil = (offerEnd < redemptionEnd ? offerEnd : redemptionEnd).toISOString();
        } else if (offerEnd) {
            validUntil = offerEnd.toISOString();
        } else if (redemptionEnd) {
            validUntil = redemptionEnd.toISOString();
        }

        return {
            title,
            type,
            percentageOff: percentageOff ? Number(percentageOff) : 0,
            amountOff: amountOff ? Number(amountOff) : 0,
            freeItem,
            minimumAmount: minimumAmount ? Number(minimumAmount) : 0,
            minimumAmountCurrency,
            validUntil,
            coverPhotoEnabled,
            coverPhotoUrl: coverPhotoEnabled ? coverPhotoUrl : '',
            pinEnabled,
            header,
            locations,
            appearance,
            locale: account?.locale ?? LOCALE.INTERNATIONAL,
            status: CouponStatus.Active,
        };
    };

    const sendPreviewData = () => {
        const data = getPreviewData();
        iframeRef.current?.contentWindow?.postMessage({ type: 'pl-preview-coupon', data }, '*');
    };

    const sendPreviewDataRef = useRef(sendPreviewData);
    sendPreviewDataRef.current = sendPreviewData;

    useEffect(() => {
        const controller = new AbortController();
        const handleMessage = event => {
            if (event.origin === import.meta.env.REACT_APP_SURVEY_DOMAIN) {
                const message = event.data;
                if (message.type === 'pl-preview-iframe-loaded') {
                    sendPreviewDataRef.current();
                }
            }
        };
        window.addEventListener('message', handleMessage, { signal: controller.signal });
        return () => controller.abort();
    }, []);

    useEffect(() => {
        sendPreviewData();
    }, [
        title,
        type,
        percentageOff,
        amountOff,
        freeItem,
        minimumAmount,
        minimumAmountCurrency,
        offerEndEnabled,
        offerEndDate,
        coverPhotoEnabled,
        coverPhotoUrl,
        pinEnabled,
        redemptionPeriodEnabled,
        redemptionPeriodInDays,
        locationId,
        brandId,
        locationType,
        brandAccess,
        brands,
        locationsMap,
    ]);

    return (
        <>
            <Typography
                variant="caption"
                sx={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '16px', textAlign: 'center' }}
            >
                <InfoOutlinedIcon style={{ transform: 'translateY(4px)', marginRight: '8px' }} />
                <Trans t={t} i18nKey={'FeedbackSurveys.changeAppearance'}>
                    <InternalLink
                        to={RoutePath.AccountSettingsBrands}
                        sx={{ color: 'var(--primary-main)', cursor: 'pointer', textDecoration: 'none' }}
                    >
                        {{ brandSettings: t('FeedbackSurveys.brandSettings') }}
                    </InternalLink>
                </Trans>
            </Typography>
            <PhonePreviewFrame>
                <iframe
                    ref={iframeRef}
                    allow="autoplay"
                    width="100%"
                    height="100%"
                    src={`${import.meta.env.REACT_APP_SURVEY_DOMAIN}/preview/coupon`}
                    frameBorder="0"
                ></iframe>
            </PhonePreviewFrame>
        </>
    );
};

export default PreviewCoupon;
